import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Users,
  Repeat,
  CalendarDays,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Home,
  ChevronLeft,
  Menu,
  Database,
  Download,
  ClipboardList,
} from 'lucide-react';
import { GlobalSearch } from '../components/GlobalSearch';
import { AppLauncherDropdown } from '../components/AppLauncherDropdown';
import { NotificationsBell } from '../components/NotificationsBell';
import { useSidebarState, type SidebarMode } from '../components/useSidebarState';
import {
  useMobileViewMode,
  type MobileViewMode,
} from '../components/useMobileViewMode';
import { AgentationWidget } from '../components/AgentationWidget';
import { useAuth } from '../contexts/AuthContext';
import MobileStartPage from '../views/mobile/MobileStartPage';
import { MobileViewModeToggle } from '../views/mobile/MobileViewModeToggle';

type NavItem = {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
  // When set, the link is only rendered for users whose role is in
  // this list. Used to keep MEMBERs away from admin-only destinations
  // they can't actually load (e.g. the People's time off calendar,
  // which gates on OWNER/ADMIN per time-view-spec §6.2).
  roles?: ReadonlyArray<'OWNER' | 'ADMIN' | 'MEMBER'>;
};

const NAV: ReadonlyArray<NavItem> = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/employees', label: 'People', icon: Users },
  { to: '/onboarding', label: 'Flows', icon: Repeat },
  // T-259 — sidebar `Time Off` entry removed. The per-employee Time
  // Off CTA now lives on the Home hero card (it opens the Request
  // Time Off modal there). The `/time-off` ROUTE is still mounted in
  // App.tsx so deep links keep working, just unlinked from the
  // chrome.
  //
  // Time view — T-143 (time-view-spec.md §2). Top-level People's time
  // off page (calendar + manage requests). Kept in the sidebar because
  // it's the HR/manager-facing roster view. T-261 — hidden for MEMBERs
  // because both underlying endpoints (`/api/timeoff/calendar` +
  // `/api/timeoff/requests`) require OWNER/ADMIN; the link would
  // otherwise dead-end on a 403.
  { to: '/time', label: 'Time', icon: CalendarDays, end: true, roles: ['OWNER', 'ADMIN'] },
  // Reviews — T-281 (review-cycles-implementation-plan.md §6). Admin
  // overview of all review cycles with completion stats. HR-only.
  { to: '/reviews', label: 'Reviews', icon: ClipboardList, end: true, roles: ['OWNER', 'ADMIN'] },
  // Docs view — T-207 (docs-view-implementation-plan.md §6). Top-level
  // org-wide Company Templates library. Per spec §15 Resolved Q12, HR-only.
  // The sidebar link is hidden for MEMBERs (the route itself renders a
  // 403 fallback when accessed directly, per T-211). HR users are ADMIN
  // role on the UI side; the server-side canSeeDocsNav helper (T-209)
  // gates detailed audience visibility.
  { to: '/docs', label: 'Docs', icon: FileText, roles: ['OWNER', 'ADMIN'] },
  // Analytics — T-090 (analytics-view-spec.md §2). Sits after Docs +
  // before Settings per plan §2. The route lazy-loads the heavy
  // recharts bundle in T-098.
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  // Setup organization — T-124 (org-setup-flow-implementation-plan
  // §7). Renamed from "Data Import" (T-116) per spec §11. Listed
  // BEFORE the Settings entry so the active-nav indicator picks the
  // more-specific URL match (NAV findIndex stops at the first hit and
  // `/settings/foo` would otherwise highlight the generic Settings
  // row). When the proper Settings shell ships, this collapses into a
  // sub-nav under Settings.
  { to: '/settings/setup-organization', label: 'Setup organization', icon: Database },
  // T-253 — Team members admin (Settings sub-page). Listed
  // BEFORE the generic Settings entry so the active-nav indicator
  // picks the more-specific URL match first.
  { to: '/settings/team-members', label: 'Team members', icon: Users },
  // T-266 — Data export (admin-only CSV bundle). Lives between
  // Team members and the generic Settings entry so the active-nav
  // indicator picks the more-specific URL match first.
  { to: '/settings/data-export', label: 'Data export', icon: Download },
  { to: '/settings', label: 'Settings', icon: Settings },
];

// Active-nav-index resolution. Exported for unit testing.
//
// Match rules per spec §4 / NavLink semantics:
//   - When `end: true` (Home) — exact pathname match only.
//   - Otherwise — pathname starts with `to`. Sub-routes like
//     `/employees/123/profile` keep the People row highlighted.
//
// Returns -1 when nothing matches (e.g. /login, /bootstrap), which
// the indicator renders as "hidden".
export function resolveActiveNavIndex(
  pathname: string,
  nav: ReadonlyArray<{ to: string; end?: boolean }> = NAV,
): number {
  return nav.findIndex((item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}

// Filters NAV by the caller's role and feature flags. Items without
// a `roles` list are always visible for all roles. Exported so the
// active-nav-index resolver in tests can be threaded through the same
// shape the rendered sidebar uses.
export function navForRole(
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null | undefined,
  nav: ReadonlyArray<NavItem> = NAV,
): ReadonlyArray<NavItem> {
  return nav.filter((item) => {
    // Role filter. The org-feature gate that used to live here went out
    // with the feature that was its only user, and the flag column it
    // read no longer exists. Re-add it when a gated nav row returns.
    if (item.roles && (role == null || !item.roles.includes(role))) {
      return false;
    }
    return true;
  });
}

export default function AppShell() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebar = useSidebarState();
  const mobile = useMobileViewMode();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  // T-252 — when the viewport is phone-sized (≤767px) AND the user
  // hasn't explicitly switched to "advanced", render a stripped-down
  // shell with two big CTAs + the manager approvals list. We compute
  // the flag here but defer the actual early return until after every
  // hook below has run, so the hook order stays stable across mode
  // switches (otherwise React panics with "rendered more hooks").
  const simpleMobile = mobile.isMobile && mobile.viewMode === 'simple';

  // ── Active-nav indicator dot (T-034) ──────────────────────────────
  const navContainerRef = useRef<HTMLElement | null>(null);
  const navRowRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [indicatorTop, setIndicatorTop] = useState<number | null>(null);
  // Filter the nav by the caller's role, then resolve the
  // active index against the filtered list — otherwise the indicator
  // jumps to the wrong row for MEMBERs (a hidden /time row would
  // still claim an index in the unfiltered NAV).
  const visibleNav = navForRole(me?.user.role, NAV);
  const activeIndex = resolveActiveNavIndex(location.pathname, visibleNav);

  useLayoutEffect(() => {
    if (activeIndex < 0) {
      setIndicatorTop(null);
      return;
    }
    const row = navRowRefs.current[activeIndex];
    if (!row) {
      setIndicatorTop(null);
      return;
    }
    setIndicatorTop(row.offsetTop + row.offsetHeight / 2 - 3);
  }, [activeIndex, location.pathname, sidebar.mode, sidebar.mobileDrawerOpen]);

  useEffect(() => {
    function onResize() {
      if (activeIndex < 0) return;
      const row = navRowRefs.current[activeIndex];
      if (row) setIndicatorTop(row.offsetTop + row.offsetHeight / 2 - 3);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeIndex]);

  // ── Mobile drawer side effects ────────────────────────────────────
  // Body scroll lock + Esc-to-close while the drawer is open.
  useEffect(() => {
    if (sidebar.mode !== 'mobile' || !sidebar.mobileDrawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') sidebar.closeMobileDrawer();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [sidebar]);

  const showDesktopSidebar = sidebar.mode !== 'mobile';
  const collapsed = sidebar.mode === 'desktop-collapsed';
  const sidebarWidthClass = collapsed ? 'w-20' : 'w-64';
  const mainOffsetClass = !showDesktopSidebar
    ? 'ml-0'
    : collapsed
      ? 'ml-20'
      : 'ml-64';

  // Now that every hook has run, we can branch.
  if (simpleMobile) {
    return (
      <MobileSimpleShell
        orgName={me?.organization?.name ?? ''}
        onSignOut={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex text-slate-900">
      {/* Desktop fixed sidebar */}
      {showDesktopSidebar && (
        <SidebarColumn
          mode={sidebar.mode}
          widthClass={sidebarWidthClass}
          collapsed={collapsed}
          showCollapseToggle
          onCollapseToggle={sidebar.toggleCollapsed}
          navContainerRef={navContainerRef}
          navRowRefs={navRowRefs}
          indicatorTop={indicatorTop}
          me={me}
          nav={visibleNav}
          onSignOut={handleLogout}
        />
      )}

      {/* Mobile slide-over drawer + backdrop */}
      {sidebar.mode === 'mobile' && sidebar.mobileDrawerOpen && (
        <>
          <div
            data-testid="mobile-drawer-backdrop"
            onClick={sidebar.closeMobileDrawer}
            className="fixed inset-0 bg-slate-900/50 z-40 transition-opacity"
            aria-hidden="true"
          />
          <SidebarColumn
            mode="mobile"
            widthClass="w-[280px]"
            collapsed={false}
            showCollapseToggle={false}
            onCollapseToggle={() => undefined}
            navContainerRef={navContainerRef}
            navRowRefs={navRowRefs}
            indicatorTop={indicatorTop}
            me={me}
            nav={visibleNav}
            onSignOut={handleLogout}
            onNavClick={sidebar.closeMobileDrawer}
            extraClass="animate-[slide-in-left_200ms_ease-out]"
          />
        </>
      )}

      <AgentationWidget />
      <main className={`flex-1 ${mainOffsetClass} transition-[margin] duration-200`}>
        {/*
         * Header per spec §5.1: 80px tall, purple-on-purple with the
         * sidebar so the top-left of the page reads as one continuous
         * brand-purple shape. Sticky so it stays pinned while content
         * scrolls.
         */}
        <header className="h-20 bg-[#6344C6] border-b border-white/10 px-4 lg:px-8 flex items-center gap-4 lg:gap-6 sticky top-0 z-40">
          {sidebar.mode === 'mobile' && (
            <button
              type="button"
              onClick={sidebar.openMobileDrawer}
              aria-label="Open navigation menu"
              data-testid="mobile-hamburger"
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <Menu size={20} />
            </button>
          )}
          <p className="text-xs font-semibold uppercase tracking-widest text-white/60 hidden sm:block">
            {me?.organization?.name}
          </p>
          <div className="flex-1 flex justify-center">
            <GlobalSearch />
          </div>
          <NotificationsBell />
          {/* T-259 — Clock-in/out moved from a persistent header pill
              to the Home hero card. The HeaderClockInPill component
              was removed. */}
          {mobile.isMobile && (
            <ViewModeToggle
              viewMode={mobile.viewMode}
              onChange={mobile.setViewMode}
            />
          )}
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ── Sidebar column (shared between desktop fixed + mobile drawer) ──
//
// Renders the App Launcher trigger, brand wordmark, NAV items with
// the active-nav indicator, the user chip, the log-out shortcut, and
// the collapse toggle (when applicable).

function SidebarColumn({
  mode,
  widthClass,
  collapsed,
  showCollapseToggle,
  onCollapseToggle,
  navContainerRef,
  navRowRefs,
  indicatorTop,
  me,
  nav,
  onSignOut,
  onNavClick,
  extraClass = '',
}: {
  mode: SidebarMode;
  widthClass: string;
  collapsed: boolean;
  showCollapseToggle: boolean;
  onCollapseToggle: () => void;
  navContainerRef: React.MutableRefObject<HTMLElement | null>;
  navRowRefs: React.MutableRefObject<Array<HTMLAnchorElement | null>>;
  indicatorTop: number | null;
  me: ReturnType<typeof useAuth>['me'];
  nav: ReadonlyArray<NavItem>;
  onSignOut: () => void;
  onNavClick?: () => void;
  extraClass?: string;
}) {
  return (
    <aside
      data-testid={mode === 'mobile' ? 'mobile-drawer' : 'desktop-sidebar'}
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`${widthClass} bg-[#6344C6] text-white fixed h-full z-50 flex flex-col transition-[width] duration-200 ${extraClass}`}
    >
      <div
        className={`p-6 flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}
      >
        <AppLauncherDropdown />
        {!collapsed && (
          <span className="font-bold text-lg tracking-tight">
            PeopleAwesomeness
          </span>
        )}
      </div>
      <nav
        ref={navContainerRef}
        className="flex-1 px-3 py-2 space-y-1 relative"
      >
        {indicatorTop !== null && (
          <span
            aria-hidden="true"
            data-testid="active-nav-indicator"
            className="absolute right-6 w-1.5 h-1.5 bg-white rounded-full pointer-events-none transition-transform duration-200 ease-out"
            style={{ top: 0, transform: `translateY(${indicatorTop}px)` }}
          />
        )}
        {nav.map((item, index) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              ref={(el) => {
                navRowRefs.current[index] = el;
              }}
              onClick={() => onNavClick?.()}
              className={({ isActive }) =>
                `w-full flex items-center ${collapsed ? 'justify-center' : 'gap-4'} px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-white/10 text-white shadow-sm'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`
              }
              data-testid={`nav-${item.to === '/' ? 'home' : item.to.slice(1)}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={20} />
              {!collapsed && (
                <span className="font-medium">{item.label}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
      {showCollapseToggle && (
        <div className="px-3 pb-2 flex justify-end">
          <button
            type="button"
            onClick={onCollapseToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-toggle"
            className="w-8 h-8 rounded-lg text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <ChevronLeft
              size={16}
              className={`transition-transform duration-200 ${
                collapsed ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      )}
      <div className="p-4 border-t border-white/10">
        <UserChip
          name={me?.user.name ?? ''}
          avatarUrl={me?.user.avatarUrl ?? null}
          organizationName={me?.organization?.name ?? ''}
          employeeId={me?.user.employeeId ?? null}
          onSignOut={onSignOut}
          collapsed={collapsed}
        />
        <button
          onClick={onSignOut}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-center gap-2'} text-xs text-white/60 hover:text-white py-2 rounded-lg hover:bg-white/5 transition-colors`}
          aria-label="Log out (sidebar shortcut)"
          title={collapsed ? 'Log out' : undefined}
        >
          <LogOut size={14} />
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
}

// ── Mobile simple shell (T-252, mobile-view-mode-spec.md §4.1) ─────
//
// Stripped-down chrome shown when viewport ≤767px AND
// `mobile.viewMode === 'simple'`. Replaces the entire sidebar +
// header + Outlet stack with a 64px purple top band (org name on
// the left, view-mode toggle pill on the right) and the
// <MobileSimpleView /> body.

function MobileSimpleShell({
  orgName,
  onSignOut,
}: {
  orgName: string;
  viewMode?: never;
  onSetViewMode?: never;
  onSignOut: () => void;
}) {
  return (
    <div
      className="min-h-screen bg-[#F8F9FB] flex flex-col text-slate-900"
      data-testid="mobile-simple-shell"
    >
      <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100 sticky top-0 z-40">
        <p
          className="text-base font-bold text-slate-800"
          data-testid="mobile-simple-org-name"
        >
          HiSarah
        </p>
        <MobileViewModeToggle />
      </header>
      <MobileStartPage />
      <footer className="px-4 py-5 text-center">
        <button
          type="button"
          onClick={onSignOut}
          data-testid="mobile-simple-signout"
          className="text-xs text-slate-500 underline-offset-2 hover:underline"
        >
          Sign out
        </button>
      </footer>
    </div>
  );
}

// ── View-mode toggle pill (used in both simple + advanced headers) ──

export function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: MobileViewMode;
  onChange: (next: MobileViewMode) => void;
}) {
  return (
    <div
      data-testid="mobile-view-mode-toggle"
      className="inline-flex rounded-full bg-white/10 p-0.5"
    >
      {(['simple', 'advanced'] as const).map((option) => {
        const active = viewMode === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={active}
            data-testid={`mobile-view-mode-${option}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              active
                ? 'bg-white text-[#6344C6]'
                : 'text-white/80 hover:text-white'
            }`}
          >
            {option === 'simple' ? 'Simple' : 'Advanced'}
          </button>
        );
      })}
    </div>
  );
}

// Sidebar identity chip. Click opens a 3-item dropdown:
// Profile / Notification preferences / Sign out (T-033, plan §5.5).
// Exported so unit tests can render it in isolation.
export function UserChip({
  name,
  avatarUrl,
  organizationName,
  employeeId,
  onSignOut,
  collapsed = false,
}: {
  name: string;
  avatarUrl: string | null;
  organizationName: string;
  employeeId: string | null;
  onSignOut?: () => void;
  collapsed?: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const inner = (
    <>
      <div className="w-10 h-10 rounded-full bg-white/20 ring-1 ring-white/10 flex items-center justify-center text-white font-bold text-sm overflow-hidden flex-shrink-0">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          name.slice(0, 1).toUpperCase()
        )}
      </div>
      {!collapsed && (
        <div className="min-w-0 text-left">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-xs text-white/60 truncate">
            {employeeId ? organizationName : 'No profile yet'}
          </p>
        </div>
      )}
    </>
  );

  // No-employee fallback — chip is a non-interactive div with the
  // "No profile yet" hint. Same behavior as before T-033.
  if (!employeeId) {
    return (
      <div
        className={`flex items-center gap-3 mb-3 opacity-80 ${collapsed ? 'justify-center' : ''}`}
        data-testid="user-chip"
      >
        {inner}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative mb-3" data-testid="user-chip">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Open ${name}'s account menu`}
        title={collapsed ? name : undefined}
        className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} -mx-2 px-2 py-1 rounded-lg hover:bg-white/10 transition-colors`}
        data-testid="user-chip-trigger"
      >
        {inner}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50 text-slate-700 min-w-[200px]"
          data-testid="user-chip-menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`/employees/${employeeId}`);
            }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
            data-testid="user-chip-item-profile"
          >
            Profile
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/settings/notifications');
            }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
            data-testid="user-chip-item-notifications"
          >
            Notification preferences
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut?.();
            }}
            disabled={!onSignOut}
            className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            data-testid="user-chip-item-signout"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
